import Dexie, { type Table } from "dexie";
import type {
  Broker,
  BrokerContact,
  CostProfile,
  Detention,
  Document,
  Driver,
  Expense,
  Load,
  LoadEvent,
  LoadStop,
  PayContract,
  Trailer,
  Truck,
} from "@haulsheet/core";
import type { DeadLetterRow, FileQueueRow, MetaRow, OutboxRow } from "./types";

export const ENTITY_TABLES = [
  "driver",
  "truck",
  "trailer",
  "pay_contract",
  "broker",
  "broker_contact",
  "load",
  "load_stop",
  "load_event",
  "document",
  "expense",
  "detention",
  "cost_profile",
] as const;

export type EntityTable = (typeof ENTITY_TABLES)[number];

export class HaulSheetDB extends Dexie {
  driver!: Table<Driver, string>;
  truck!: Table<Truck, string>;
  trailer!: Table<Trailer, string>;
  pay_contract!: Table<PayContract, string>;
  broker!: Table<Broker, string>;
  broker_contact!: Table<BrokerContact, string>;
  load!: Table<Load, string>;
  load_stop!: Table<LoadStop, string>;
  load_event!: Table<LoadEvent, string>;
  document!: Table<Document, string>;
  expense!: Table<Expense, string>;
  detention!: Table<Detention, string>;
  cost_profile!: Table<CostProfile, string>;
  outbox!: Table<OutboxRow, string>;
  deadLetter!: Table<DeadLetterRow, string>;
  fileQueue!: Table<FileQueueRow, string>;
  meta!: Table<MetaRow, string>;

  constructor(userId: string) {
    super(`haulsheet-${userId}`);
    const stores = {
      driver: "id, orgId, userId, updatedAt, deletedAt",
      truck: "id, orgId, driverId, updatedAt, deletedAt",
      trailer: "id, orgId, updatedAt, deletedAt",
      pay_contract: "id, orgId, active, updatedAt, deletedAt",
      broker: "id, orgId, name, updatedAt, deletedAt",
      broker_contact: "id, orgId, brokerId, updatedAt, deletedAt",
      load: "id, orgId, status, driverId, updatedAt, deletedAt",
      load_stop: "id, orgId, loadId, seq, updatedAt, deletedAt",
      load_event: "id, orgId, loadId, at, updatedAt, deletedAt",
      document: "id, orgId, loadId, kind, updatedAt, deletedAt",
      expense: "id, orgId, kind, occurredOn, updatedAt, deletedAt",
      detention: "id, orgId, loadId, arrivedAt, updatedAt, deletedAt",
      cost_profile: "id, orgId, driverId, updatedAt, deletedAt",
      outbox: "mutationId, [entity+id], entity, id, createdAt",
      deadLetter: "mutationId, at",
      fileQueue: "id, createdAt",
      meta: "key",
    };
    this.version(1).stores(stores);
    // v2: compound outbox index for coalesce lookups
    this.version(2).stores(stores);
  }
}

let current: HaulSheetDB | null = null;
let currentUserId: string | null = null;

export function openDb(userId: string): HaulSheetDB {
  if (current && currentUserId === userId) return current;
  if (current) current.close();
  current = new HaulSheetDB(userId);
  currentUserId = userId;
  return current;
}

export function getDb(): HaulSheetDB {
  if (!current) throw new Error("Dexie not opened — call openDb(userId) first");
  return current;
}

export function closeDb(): void {
  if (current) current.close();
  current = null;
  currentUserId = null;
}

export function entityTable(db: HaulSheetDB, entity: EntityTable): Table<any, string> {
  return db.table(entity);
}
